import { motion } from "framer-motion";
import { ArrowRight, Heart, Quote, Truck } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useSite } from "@/context/SiteContext";
import { imgUrl } from "@/lib/api";
import { ProductCard } from "./ProductCard";

export const FONT_OPTIONS = [
  { id: "display", label: "Playfair Display — elegant serif", css: "'Playfair Display', serif" },
  { id: "accent", label: "Cormorant Garamond — refined serif", css: "'Cormorant Garamond', serif" },
  { id: "dancing", label: "Dancing Script — handwritten", css: "'Dancing Script', cursive" },
  { id: "lora", label: "Lora — classic serif", css: "'Lora', serif" },
  { id: "montserrat", label: "Montserrat — clean sans", css: "'Montserrat', sans-serif" },
  { id: "sans", label: "Manrope — modern sans", css: "'Manrope', sans-serif" },
];

export const fontCss = (id) => (FONT_OPTIONS.find((f) => f.id === id) || FONT_OPTIONS[0]).css;

export const DEFAULT_HERO_STYLE = {
  overlay: 0,
  panel: true,
  align: "left",
  heading_font: "display",
  heading_size: 54,
  heading_color: "#2C302E",
  accent_font: "dancing",
  accent_size: 38,
  accent_color: "#A05C7B",
  text_color: "#3A3F3C",
  panel_width: 55,
  panel_padding: 40,
  panel_opacity: 85,
};

export const DEFAULT_STYLES = {
  hero: DEFAULT_HERO_STYLE,
  occasions_title_color: "",
  featured_title_color: "",
  testimonials_title_color: "",
  gallery_title_color: "",
  features_bg: "",
  promo_bg: "",
};

export const DEFAULT_SECTION_ORDER = ["occasions", "features", "accreditation", "featured", "promo", "testimonials", "gallery", "advert"];

export const SECTION_LABELS = {
  hero: "Hero",
  occasions: "Occasion cards",
  features: "Green feature band",
  accreditation: "Good Florist Guide strip",
  featured: "Featured products",
  promo: "Promo band",
  testimonials: "Testimonials",
  gallery: "Gallery strip",
  advert: "Heavenly Gems advert",
};

export function mergeStyles(styles) {
  const s = styles || {};
  return { ...DEFAULT_STYLES, ...s, hero: { ...DEFAULT_HERO_STYLE, ...(s.hero || {}) } };
}

function luminance(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return 1;
  const int = parseInt(m[1], 16);
  const r = (int >> 16) & 255, g = (int >> 8) & 255, b = int & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

const TILTS = ["-rotate-2", "rotate-1", "-rotate-1", "rotate-2", "-rotate-1", "rotate-2"];

function HeroSection({ content, styles, editable = false, selected = false, onHeroResize, forceMobilePanel = false }) {
  const h = styles.hero;
  const containerRef = useRef(null);
  const [panelFade, setPanelFade] = useState(1);

  useEffect(() => {
    if (editable) return undefined;
    const onScroll = () => {
      const f = window.innerWidth < 640 ? Math.max(0, 1 - window.scrollY / 220) : 1;
      setPanelFade(f);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [editable]);

  const startResize = (e) => {
    if (!editable || !onHeroResize) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = h.panel_width;
    const containerW = containerRef.current?.offsetWidth || 1200;
    const onMove = (ev) => {
      const deltaPct = ((ev.clientX - startX) / containerW) * 100;
      onHeroResize(Math.round(Math.min(100, Math.max(30, startW + deltaPct))));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <section className="relative" data-testid="hero-section" ref={containerRef}>
      <div className="absolute inset-0">
        {content?.hero_image && <img src={imgUrl(content.hero_image)} alt="" className="h-full w-full object-cover" />}
        {h.overlay > 0 && <div className="absolute inset-0" style={{ backgroundColor: `rgba(0,0,0,${h.overlay / 100})` }} />}
      </div>
      <div className="relative max-w-7xl mx-auto px-4 py-20 sm:py-28 lg:py-36">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className={`relative ${h.align === "center" ? "mx-auto text-center" : ""}`}
          style={{ maxWidth: `clamp(320px, ${h.panel_width}%, 900px)` }}
        >
          <div
            className={h.panel ? `hero-panel${forceMobilePanel ? " hero-panel-force-mobile" : ""}` : ""}
            style={h.panel ? { "--hero-panel-opacity": h.panel_opacity ?? 85, "--hero-panel-pad": `${h.panel_padding ?? 40}px`, "--hero-panel-fade": panelFade, "--hero-panel-blur": 10 * panelFade } : {}}
          >            <h1
              className="tracking-tight whitespace-pre-line leading-[1.05]"
              style={{ fontFamily: fontCss(h.heading_font), fontSize: `clamp(30px, 8.5vw, ${h.heading_size}px)`, color: h.heading_color }}
              data-testid="hero-heading"
            >              {content?.hero_heading || "Beautiful flowers.\nPersonal touch."}
            </h1>
            {content?.hero_accent !== "" && (
              <p
                className="mt-3"
                style={{ fontFamily: fontCss(h.accent_font), fontSize: `clamp(24px, 6.5vw, ${h.accent_size}px)`, color: h.accent_color }}
                data-testid="hero-accent"
              >
                {content?.hero_accent || "Every time."}
              </p>
            )}
            <p className="mt-5 text-base md:text-lg max-w-xl leading-relaxed" style={{ color: h.text_color }} data-testid="hero-text">
              {content?.hero_text}
            </p>
            <div className={`mt-8 flex flex-wrap gap-3 ${h.align === "center" ? "justify-center" : ""}`}>
              <Button asChild size="lg" className="rounded-full px-8" data-testid="hero-cta-btn">
                <Link to={content?.cta_link || "/shop"}>{content?.cta_label || "Shop Now"} <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              {content?.secondary_cta_label && (
                <Button asChild size="lg" variant="outline" className="rounded-full px-8" data-testid="hero-secondary-cta-btn">
                  <Link to={content.secondary_cta_link || "/contact"}>{content.secondary_cta_label}</Link>
                </Button>
              )}
            </div>
          </div>
          {editable && selected && !forceMobilePanel && (
            <>
              <div
                onPointerDown={startResize}
                className="absolute top-1/2 -translate-y-1/2 -right-3 w-6 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center cursor-ew-resize shadow-lg hover:scale-110 transition-transform"
                data-testid="hero-resize-handle"
                title="Drag to resize the text box"
              >
                <span className="text-xs font-bold select-none">⇔</span>
              </div>
              <span className="absolute -bottom-7 left-0 text-xs bg-primary text-primary-foreground rounded-full px-2.5 py-1 shadow" data-testid="hero-width-readout">
                {h.panel_width}% wide
              </span>
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
}

function OccasionsSection({ content, styles, data }) {
  if (content?.show_categories === false || !data.occasionsChildren.length) return null;
  const titleStyle = styles.occasions_title_color ? { color: styles.occasions_title_color } : {};
  return (
    <section className="max-w-7xl mx-auto px-4 pt-16 sm:pt-20" data-testid="home-occasions">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Shop by occasion</p>
          <h2 className="font-display text-3xl tracking-tight mt-2" style={titleStyle}>Popular Occasions</h2>
        </div>
        <Link to="/shop" className="text-sm text-primary hover:underline hidden sm:flex items-center gap-1" data-testid="view-all-categories-link">
          View all <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-5">
        {data.occasionsChildren.slice(0, 6).map((cat, i) => (
          <motion.div key={cat.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(i * 0.07, 0.4) }}>
            <Link
              to={`/shop?category=${cat.slug}`}
              data-testid={`polaroid-${cat.slug}`}
              className={`group block bg-card p-2.5 pb-3 rounded-md shadow-md border transition-all duration-300 hover:shadow-xl hover:rotate-0 hover:-translate-y-1 ${TILTS[i % TILTS.length]}`}
            >
              <div className="overflow-hidden rounded-sm aspect-square bg-muted">
                {cat.image && <img src={imgUrl(cat.image)} alt={cat.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
              </div>
              <p className="font-accent italic text-lg sm:text-xl text-center mt-2.5">{cat.name}</p>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function FeaturesSection({ content, styles, data }) {
  if (content?.show_features === false) return null;
  const custom = styles.features_bg;
  const dark = custom ? luminance(custom) < 0.55 : true;
  const style = custom ? { backgroundColor: custom } : {};
  const textCls = dark ? "text-white" : "text-neutral-800";
  const subCls = dark ? "text-white/80" : "text-neutral-600";
  const quoteCls = dark ? "text-white/70" : "text-neutral-500";
  return (
    <section className={`mt-16 sm:mt-20 ${custom ? "" : "bg-primary text-primary-foreground"}`} style={style} data-testid="home-features">
      <div className={`max-w-7xl mx-auto px-4 py-14 sm:py-16 grid md:grid-cols-3 gap-10 ${custom ? textCls : ""}`}>
        <div>
          <Heart className="h-7 w-7 mb-4 opacity-80" />
          <h3 className="font-display text-2xl tracking-tight">{content?.feature_local_title || "Why choose local?"}</h3>
          <p className={`mt-2 text-sm leading-relaxed ${custom ? subCls : "text-primary-foreground/80"}`}>{content?.feature_local_text}</p>
        </div>
        <div className={`md:border-x md:px-10 ${custom ? (dark ? "border-white/20" : "border-black/15") : "md:border-primary-foreground/20"}`}>
          <Quote className="h-7 w-7 mb-4 opacity-80" />
          {data.testimonials[0] ? (
            <blockquote>
              <p className="font-accent italic text-xl leading-relaxed">“{data.testimonials[0].text}”</p>
              <footer className={`mt-3 text-sm ${custom ? quoteCls : "text-primary-foreground/70"}`}>— {data.testimonials[0].name}</footer>
            </blockquote>
          ) : (
            <p className="font-accent italic text-xl leading-relaxed">“The most beautiful flowers, made with genuine care.”</p>
          )}
        </div>
        <div>
          <Truck className="h-7 w-7 mb-4 opacity-80" />
          <h3 className="font-display text-2xl tracking-tight">{content?.feature_delivery_title || "Same Day Delivery"}</h3>
          <p className={`mt-2 text-sm leading-relaxed ${custom ? subCls : "text-primary-foreground/80"}`}>{content?.feature_delivery_text}</p>
        </div>
      </div>
    </section>
  );
}

function AccreditationSection({ content, styles, data }) {
  const badge = data.settings?.branding?.accreditation_badge;
  if (!badge || content?.show_accreditation === false) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 pt-14" data-testid="home-accreditation">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="rounded-2xl border bg-card px-8 py-8 sm:px-12 flex flex-col sm:flex-row items-center gap-6 sm:gap-10 text-center sm:text-left">
        <img src={imgUrl(badge)} alt="We are in The Good Florist Guide 2025–2026" className="h-28 w-28 sm:h-36 sm:w-36 object-contain shrink-0" data-testid="gfg-badge-home" />
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Recognised for quality</p>
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight mt-2">We're in The Good Florist Guide</h2>
          <p className="text-muted-foreground mt-2 max-w-xl">Independently recognised among the UK's finest florists for 2025–2026 — quality flowers at affordable prices, as always.</p>
        </div>
      </motion.div>
    </section>
  );
}

function FeaturedSection({ content, styles, data }) {
  if (content?.show_featured === false) return null;
  const titleStyle = styles.featured_title_color ? { color: styles.featured_title_color } : {};
  return (
    <section className="max-w-7xl mx-auto px-4 py-8 sm:py-12 mt-8" data-testid="home-featured">
      <div className="flex items-end justify-between mb-8">
        <h2 className="font-display text-3xl tracking-tight" style={titleStyle}>Featured Flowers</h2>
        <Link to="/shop" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="shop-all-link">
          Shop all <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      {data.featured === null ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {[...Array(4)].map((_, i) => <div key={i} className="aspect-[4/5] rounded-xl bg-muted animate-pulse" />)}
        </div>
      ) : data.featured.length === 0 ? (
        <p className="text-muted-foreground" data-testid="featured-empty">No featured products right now — browse the full shop instead.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {data.featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </section>
  );
}

const DAY_COUNT_WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven"];

function PromoSection({ content, styles }) {
  const { settings } = useSite();
  if (!content?.promo_heading || content?.show_promo === false) return null;
  const custom = styles.promo_bg;
  const dark = custom ? luminance(custom) < 0.55 : false;
  const style = custom ? { backgroundColor: custom } : {};
  const daysArr = settings?.delivery?.days;
  const n = Array.isArray(daysArr) && daysArr.length ? daysArr.length : 6;
  const heading = (content.promo_heading || "").replace(
    /(one|two|three|four|five|six|seven|\d+)\s+days?\s+a\s+week/i,
    `${DAY_COUNT_WORDS[n]} ${n === 1 ? "day" : "days"} a week`
  );
  return (
    <section className="max-w-7xl mx-auto px-4 py-12" data-testid="home-promo">
      <div className={`rounded-2xl px-8 py-12 sm:px-12 flex flex-col sm:flex-row items-start sm:items-center gap-6 ${custom ? "" : "bg-secondary/60 dark:bg-secondary/20"}`} style={style}>
        <div className={`p-3 rounded-full shrink-0 ${custom ? (dark ? "bg-white/15 text-white" : "bg-black/10 text-neutral-800") : "bg-primary/10 text-primary"}`}>
          <Truck className="h-6 w-6" />
        </div>
        <div className={custom ? (dark ? "text-white" : "text-neutral-800") : ""}>
          <h2 className="font-display text-2xl tracking-tight">{heading}</h2>
          <p className={`mt-2 max-w-2xl ${custom ? (dark ? "text-white/80" : "text-neutral-600") : "text-muted-foreground"}`}>{content.promo_text}</p>
        </div>
        <Button asChild variant="outline" className={`rounded-full sm:ml-auto shrink-0 ${custom && dark ? "border-white/50 text-white hover:bg-white/10" : ""}`} data-testid="promo-delivery-link">
          <Link to="/delivery">Delivery details</Link>
        </Button>
      </div>
    </section>
  );
}

function TestimonialsSection({ content, styles, data }) {
  if (content?.show_testimonials === false || !data.testimonials.length) return null;
  const titleStyle = styles.testimonials_title_color ? { color: styles.testimonials_title_color } : {};
  return (
    <section className="max-w-7xl mx-auto px-4 py-12 sm:py-16" data-testid="home-testimonials">
      <h2 className="font-display text-3xl tracking-tight text-center mb-10" style={titleStyle}>Kind Words</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {data.testimonials.slice(0, 3).map((t, i) => (
          <motion.blockquote key={t.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="rounded-xl border bg-card p-6" data-testid={`home-testimonial-${t.id}`}>
            <p className="font-accent text-lg italic leading-relaxed">“{t.text}”</p>
            <footer className="mt-4 text-sm font-semibold">{t.name}{t.category ? <span className="text-muted-foreground font-normal"> · {t.category}</span> : null}</footer>
          </motion.blockquote>
        ))}
      </div>
    </section>
  );
}

function GallerySection({ content, styles, data }) {
  if (content?.show_gallery === false || !data.gallery.length) return null;
  const titleStyle = styles.gallery_title_color ? { color: styles.gallery_title_color } : {};
  return (
    <section className="max-w-7xl mx-auto px-4 py-12 sm:py-16" data-testid="home-gallery">
      <div className="flex items-end justify-between mb-8">
        <h2 className="font-display text-3xl tracking-tight" style={titleStyle}>From the Workshop</h2>
        <Link to="/gallery" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="gallery-all-link">
          View gallery <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {data.gallery.slice(0, 6).map((g) => (
          <Link to="/gallery" key={g.id} className="group overflow-hidden rounded-xl aspect-square" data-testid={`home-gallery-${g.id}`}>
            <img src={imgUrl(g.url)} alt={g.caption || "Gallery image"} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          </Link>
        ))}
      </div>
    </section>
  );
}

const HG_DEFAULTS = {
  heading: "Our friends at Heavenly Gems",
  text: "Handcrafted memorial jewellery made with care in South Wales — beautiful keepsakes created with ashes, fingerprints or handwriting, to keep your loved ones close. Family-run and trusted by hundreds of families across the UK.",
  link: "https://heavenlygems.co.uk",
};

function AdvertSection({ content }) {
  if (content?.show_hg === false) return null;
  const logo = content?.hg_logo;
  return (
    <section className="max-w-7xl mx-auto px-4 pt-16 sm:pt-20" data-testid="home-advert">
      <div className="rounded-2xl border bg-secondary/50 dark:bg-secondary/15 px-8 py-10 flex flex-col sm:flex-row items-center gap-8 sm:gap-10 text-center sm:text-left">
        {logo && (
          <div className="bg-white rounded-xl p-3 shadow-sm shrink-0">
            <img src={imgUrl(logo)} alt="Heavenly Gems logo" className="h-14 sm:h-16 w-auto" data-testid="advert-logo" />
          </div>
        )}
        <div className="flex-1">
          <h2 className="font-display text-2xl tracking-tight" data-testid="advert-heading">{content?.hg_heading || HG_DEFAULTS.heading}</h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl" data-testid="advert-text">{content?.hg_text || HG_DEFAULTS.text}</p>
        </div>
        <Button asChild className="rounded-full shrink-0" data-testid="advert-link">
          <a href={content?.hg_link || HG_DEFAULTS.link} target="_blank" rel="noreferrer">Visit Heavenly Gems</a>
        </Button>
      </div>
    </section>
  );
}

const SECTION_RENDERERS = {
  occasions: OccasionsSection,
  features: FeaturesSection,
  accreditation: AccreditationSection,
  featured: FeaturedSection,
  promo: PromoSection,
  testimonials: TestimonialsSection,
  gallery: GallerySection,
  advert: AdvertSection,
};

export function HomeSections({ content, data, editable = false, selected = null, onSelect = () => {}, onHeroResize, forceMobilePanel = false }) {
  const styles = mergeStyles(content?.styles);
  const order = content?.section_order?.length ? content.section_order : DEFAULT_SECTION_ORDER;

  const wrap = (key, node) => {
    if (!node) return null;
    if (!editable) return <div key={key}>{node}</div>;
    return (
      <div
        key={key}
        onClick={() => onSelect(key)}
        className={`cursor-pointer transition-shadow rounded-sm ${selected === key ? "ring-2 ring-primary ring-offset-4 ring-offset-background" : "hover:ring-2 hover:ring-primary/40 hover:ring-offset-4 hover:ring-offset-background"}`}
        data-testid={`editor-section-${key}`}
      >
        {node}
      </div>
    );
  };

  const heroNode = editable ? (
    <div onClick={() => onSelect("hero")} className={`cursor-pointer transition-shadow ${selected === "hero" ? "ring-2 ring-primary ring-inset" : "hover:ring-2 hover:ring-primary/40 hover:ring-inset"}`} data-testid="editor-section-hero">
      <HeroSection content={content} styles={styles} editable selected={selected === "hero"} onHeroResize={onHeroResize} forceMobilePanel={forceMobilePanel} />
    </div>
  ) : (
    <HeroSection content={content} styles={styles} />
  );

  return (
    <>
      {heroNode}
      {order.map((key) => {
        const Renderer = SECTION_RENDERERS[key];
        if (!Renderer) return null;
        return wrap(key, <Renderer content={content} styles={styles} data={data} />);
      })}
    </>
  );
}
